// frontend/src/store/hotelContext.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getSubdomain, isOnSubdomain } from '../utils/subdomain';
import { hotelApi } from '../services/api';

// ── Logo data-URI cache ──────────────────────────────────────────────────────
// Stores the logo as a base64 data URI so repeat/same-session visits need
// zero extra network requests — eliminates the second browser-progress-bar.
// Two layers: sessionStorage (survives within the same incognito session) +
// localStorage (survives across sessions on the same device).
const LOGO_CACHE_KEY = 'sn-logo-v1';

const readLogoCache = (url) => {
  if (!url) return null;
  const tryRead = (store) => {
    try {
      const raw = store.getItem(LOGO_CACHE_KEY);
      if (!raw) return null;
      const e = JSON.parse(raw);
      return e.url === url ? e.d : null;
    } catch { return null; }
  };
  return tryRead(sessionStorage) || tryRead(localStorage);
};

const writeLogoCache = (url, d) => {
  const s = JSON.stringify({ url, d });
  try { sessionStorage.setItem(LOGO_CACHE_KEY, s); } catch {}
  try { localStorage.setItem(LOGO_CACHE_KEY, s); } catch {}
};

const fetchLogoDataUri = async (url) => {
  const hit = readLogoCache(url);
  if (hit) return hit;
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const d = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    writeLogoCache(url, d);
    return d;
  } catch { return null; }
};

// ── Branding helpers ─────────────────────────────────────────────────────────
const applyBranding = (hotel) => {
  if (!hotel) return;
  const color = hotel.primaryColor || hotel.primary_color || '#d4a853';
  document.documentElement.style.setProperty('--gold', color);
  document.title = `${hotel.name} — StayNivo HMS`;

  const logoSrc = hotel.logoDataUri || hotel.logoUrl || hotel.logo_url;
  if (logoSrc) {
    // Favicon
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = logoSrc;
    if (!document.head.contains(link)) document.head.appendChild(link);

    // Preload network URL so browser starts fetching before React renders the <img>
    const netUrl = hotel.logoUrl || hotel.logo_url;
    if (netUrl && !logoSrc.startsWith('data:')) {
      const exists = document.querySelector(`link[rel="preload"][href="${netUrl}"]`);
      if (!exists) {
        const pl = document.createElement('link');
        pl.rel = 'preload'; pl.as = 'image'; pl.href = netUrl;
        document.head.appendChild(pl);
      }
    }
  }
};

// ── Sync read from localStorage ──────────────────────────────────────────────
// Gives the initial Zustand state access to cached hotel data + logo URI before
// any React render so the logo is available on the very first paint.
const getSyncPersisted = () => {
  try {
    const raw = localStorage.getItem('hotel-context');
    if (!raw) return { hotel: null, subdomain: null };
    const state = JSON.parse(raw).state || { hotel: null, subdomain: null };
    if (state.hotel) {
      // Normalise: API uses camelCase; Layout.js checks snake_case
      if (state.hotel.logoUrl && !state.hotel.logo_url) state.hotel.logo_url = state.hotel.logoUrl;
      // Attach cached logo data URI so first render uses it immediately
      const url = state.hotel.logoUrl || state.hotel.logo_url;
      const hit = readLogoCache(url);
      if (hit) state.hotel.logoDataUri = hit;
    }
    return state;
  } catch { return { hotel: null, subdomain: null }; }
};

const _synced           = getSyncPersisted();
const _currentSubdomain = getSubdomain();
const _hasCachedData    = !!(_synced.hotel && _synced.subdomain === _currentSubdomain);

// Apply branding synchronously before React renders (non-incognito repeat visits)
if (_hasCachedData) applyBranding(_synced.hotel);

// ── Store ────────────────────────────────────────────────────────────────────
export const useHotelContext = create(
  persist(
    (set, get) => ({
      hotel:     _hasCachedData ? _synced.hotel : null,
      subdomain: _currentSubdomain,
      isLoading: isOnSubdomain() && !_hasCachedData,
      error:     null,
      notFound:  false,

      loadHotelFromSubdomain: async () => {
        const subdomain = getSubdomain();
        const cached    = get().hotel;
        const hasCached = !!(cached && get().subdomain === subdomain);

        if (hasCached) {
          set({ subdomain, error: null, notFound: false });
        } else {
          set({ subdomain, isLoading: true, error: null, notFound: false });
        }

        if (!isOnSubdomain()) {
          set({ hotel: null, isLoading: false });
          return;
        }

        try {
          const res   = await hotelApi.info();
          const hotel = res.data.data;

          if (hotel.logoUrl   && !hotel.logo_url)      hotel.logo_url      = hotel.logoUrl;
          if (hotel.primaryColor && !hotel.primary_color) hotel.primary_color = hotel.primaryColor;

          const logoUrl = hotel.logoUrl || hotel.logo_url;
          if (logoUrl) {
            const hit = readLogoCache(logoUrl);
            if (hit) {
              hotel.logoDataUri = hit;
            } else {
              fetchLogoDataUri(logoUrl).then(d => {
                if (d) set(s => ({ hotel: s.hotel ? { ...s.hotel, logoDataUri: d } : s.hotel }));
              });
            }
          }

          set({ hotel, isLoading: false });
          applyBranding(hotel);
        } catch (err) {
          const code = err.response?.data?.code;
          if (code === 'HOTEL_NOT_FOUND' || err.response?.status === 404) {
            set({ notFound: true, isLoading: false });
          } else {
            set({ error: err.message, isLoading: false });
          }
        }
      },

      setHotelFromAuth: (user) => {
        set((state) => ({
          hotel: state.hotel
            ? { ...state.hotel, ...user }
            : { name: user.hotelName, currency: user.currency, currencySymbol: user.currencySymbol },
        }));
      },
    }),
    {
      name: 'hotel-context',
      partialize: (state) => ({ hotel: state.hotel, subdomain: state.subdomain }),
    }
  )
);
